import React, { useEffect, useState } from 'react';
import { View, Text, LayoutChangeEvent } from 'react-native';
import Svg, { Rect, Text as SvgText, Line } from 'react-native-svg';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';

interface GroupedBarChartProps {
  data: { label: string; values: number[] }[];
  seriesColors: string[];
  seriesLabels: string[];
  height?: number;
}

const PADDING = { top: 8, right: 8, bottom: 28, left: 36 };
const BAR_GROUP_GAP = 8;
const BAR_GAP = 2;

export function GroupedBarChart({
  data,
  seriesColors,
  seriesLabels,
  height = 180,
}: GroupedBarChartProps) {
  const themeColors = useThemeColors();
  const [chartWidth, setChartWidth] = useState(0);
  const clipProgress = useSharedValue(0);

  useEffect(() => {
    clipProgress.value = withTiming(1, { duration: 700 });
  }, [data]);

  const clipStyle = useAnimatedStyle(() => ({
    width: `${clipProgress.value * 100}%`,
    overflow: 'hidden' as const,
  }));

  const onLayout = (e: LayoutChangeEvent) => {
    setChartWidth(e.nativeEvent.layout.width);
  };

  if (!data.length || !chartWidth) {
    return (
      <View onLayout={onLayout} style={{ height }}>
        <View className="flex-1 items-center justify-center">
          <Text style={{ color: themeColors.text.tertiary }} className="text-xs">No data</Text>
        </View>
      </View>
    );
  }

  const innerW = chartWidth - PADDING.left - PADDING.right;
  const innerH = height - PADDING.top - PADDING.bottom;

  const numSeries = seriesColors.length;
  const numGroups = data.length;
  const groupWidth = (innerW - BAR_GROUP_GAP * (numGroups - 1)) / numGroups;
  const barWidth = (groupWidth - BAR_GAP * (numSeries - 1)) / numSeries;

  const maxVal = data.reduce((max, d) => {
    return Math.max(max, ...d.values);
  }, 1);

  const yScale = (v: number) => innerH - (v / maxVal) * innerH;

  // Grid lines
  const gridLines = [0.25, 0.5, 0.75].map((pct) => ({
    y: PADDING.top + yScale(maxVal * pct),
    label: Math.round(maxVal * pct).toString(),
  }));

  return (
    <View onLayout={onLayout} style={{ height }}>
      {chartWidth > 0 && (
        <Animated.View style={[{ height }, clipStyle]}>
          <Svg width={chartWidth} height={height}>
            {/* Grid */}
            {gridLines.map((g, i) => (
              <React.Fragment key={`grid-${i}`}>
                <Line
                  x1={PADDING.left}
                  y1={g.y}
                  x2={chartWidth - PADDING.right}
                  y2={g.y}
                  stroke={themeColors.border.light}
                  strokeWidth={1}
                  strokeDasharray="4,4"
                />
                <SvgText
                  x={PADDING.left - 6}
                  y={g.y + 3}
                  textAnchor="end"
                  fontSize={9}
                  fill={themeColors.text.tertiary}
                >
                  {g.label}
                </SvgText>
              </React.Fragment>
            ))}

            {/* Y-axis labels */}
            <SvgText
              x={PADDING.left - 6}
              y={PADDING.top + innerH + 3}
              textAnchor="end"
              fontSize={9}
              fill={themeColors.text.tertiary}
            >
              0
            </SvgText>

            {/* Bars */}
            {data.map((group, gi) => {
              const groupX = PADDING.left + gi * (groupWidth + BAR_GROUP_GAP);

              return (
                <React.Fragment key={`group-${gi}`}>
                  {group.values.map((val, si) => {
                    const barH = (val / maxVal) * innerH;
                    const barX = groupX + si * (barWidth + BAR_GAP);
                    const barY = PADDING.top + innerH - barH;

                    return (
                      <Rect
                        key={`${gi}-${si}`}
                        x={barX}
                        y={barY}
                        width={Math.max(barWidth, 2)}
                        height={Math.max(barH, 1)}
                        rx={3}
                        fill={seriesColors[si]}
                        opacity={0.8}
                      />
                    );
                  })}

                  {/* X-axis label */}
                  <SvgText
                    x={groupX + groupWidth / 2}
                    y={height - 4}
                    textAnchor="middle"
                    fontSize={9}
                    fill={themeColors.text.tertiary}
                  >
                    {group.label}
                  </SvgText>
                </React.Fragment>
              );
            })}
          </Svg>
        </Animated.View>
      )}

      {/* Legend */}
      <View className="flex-row items-center gap-4 mt-1 ml-9">
        {seriesLabels.map((label, i) => (
          <View key={`legend-${i}`} className="flex-row items-center gap-1.5">
            <View
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: seriesColors[i] }}
            />
            <Text style={{ color: themeColors.text.secondary }} className="text-[10px]">
              {label}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}
